/**
 * Btn — v.1-D §4.1.
 *
 * The five-variant primary button: primary (dark solid), secondary (outlined),
 * ghost (transparent → cta on hover), ghost-inverse (white-on-dark), and the
 * v.1-D addition `destructive` (white-on-bad). Renders as `<a>` when `href`
 * is provided, otherwise `<button>`. The `loading` state replaces the icon
 * slot with a spinner and disables interaction.
 */
import type { CSSProperties, JSX, ReactNode } from 'react';
import { cn } from '../../lib/utils';

type Variant = 'primary' | 'secondary' | 'ghost' | 'ghost-inverse' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

export interface BtnProps {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  destructive?: boolean;
  fullWidth?: boolean;
  inverse?: boolean;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

function variantStyle(variant: Variant): CSSProperties {
  switch (variant) {
    case 'primary':
      return {
        background: 'var(--ink)',
        color:      'var(--canvas)',
        border:     '1px solid var(--ink)',
      };
    case 'secondary':
      return {
        background: 'var(--canvas)',
        color:      'var(--ink)',
        border:     '1px solid var(--border-cool)',
      };
    case 'ghost':
      return {
        background: 'transparent',
        color:      'var(--ink)',
        border:     '1px solid transparent',
      };
    case 'ghost-inverse':
      return {
        background: 'transparent',
        color:      'var(--canvas)',
        border:     '1px solid transparent',
      };
    case 'destructive':
      return {
        background: 'var(--bad)',
        color:      'var(--canvas)',
        border:     '1px solid var(--bad)',
      };
  }
}

function sizeStyle(size: Size): CSSProperties {
  switch (size) {
    case 'sm':
      return { padding: '6px 10px', fontSize: 13, height: 28 };
    case 'md':
      return { padding: '8px 14px', fontSize: 14, height: 36 };
    case 'lg':
      return { padding: '10px 18px', fontSize: 15, height: 44 };
  }
}

export function Btn({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  loading = false,
  children,
  onClick,
  href,
  type = 'button',
  destructive = false,
  fullWidth = false,
  className,
  disabled = false,
  'aria-label': ariaLabel,
}: BtnProps): JSX.Element {
  const effectiveVariant: Variant = destructive && variant === 'primary' ? 'destructive' : variant;
  const isDisabled = disabled || loading;

  const style: CSSProperties = {
    ...variantStyle(effectiveVariant),
    ...sizeStyle(size),
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            8,
    borderRadius:   'var(--radius-md)',
    cursor:         isDisabled ? 'not-allowed' : 'pointer',
    fontFamily:     'var(--font-sans)',
    fontWeight:     500,
    opacity:        isDisabled ? 0.6 : 1,
    transition:     `background var(--duration-fast) var(--ease-emphasized), color var(--duration-fast) var(--ease-emphasized)`,
    width:          fullWidth ? '100%' : undefined,
    textDecoration: 'none',
  };

  const inner = (
    <>
      {loading ? <Spinner /> : icon}
      {children !== undefined && children !== null && <span>{children}</span>}
      {iconRight}
    </>
  );

  if (href !== undefined && !isDisabled) {
    return (
      <a
        href={href}
        className={cn(className)}
        style={style}
        aria-label={ariaLabel}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={cn(className)}
      style={style}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
    >
      {inner}
    </button>
  );
}

function Spinner(): JSX.Element {
  return (
    <span
      aria-hidden
      style={{
        width:        14,
        height:       14,
        border:       '2px solid currentColor',
        borderTop:    '2px solid transparent',
        borderRadius: '50%',
        animation:    'circa-shimmer 0.8s linear infinite',
        display:      'inline-block',
      }}
    />
  );
}
